import { Component, Input, OnInit } from '@angular/core';
import { EChartsOption, BarSeriesOption } from 'echarts';
import { DashboardService } from '../../../../services/dashboard/dashboard.service';
import { catchError, finalize, tap } from 'rxjs';
import { isArrayWithData } from '../../../../utilities/array.helpers';
import { AppService } from '../../../../app.service';
import moment from 'moment';
import { SelectOptionLabelValue } from '../../../../interfaces/select-option.interface';

interface GraphData {
    name: string; // Feb-1 or Week 1 or Nov
    creditor: string;
    count: number;
}

@Component({
    selector: 'trend-analysis-dashboard',
    templateUrl: './trend-analysis-dashboard.component.html',
    styleUrls: ['./trend-analysis-dashboard.component.css'],
})
export class TrendAnalysisDashboardComponent implements OnInit {
    constructor(
        private dashboardService: DashboardService,
        private appService: AppService,
    ) {}

    isArrayWithData = isArrayWithData;
    selectedCreditors?: number[];
    fromDate: Date = new Date();
    toDate: Date = new Date();
    transactionTypes: SelectOptionLabelValue[] = [];
    channels: SelectOptionLabelValue[] = [];
    statusNames: SelectOptionLabelValue[] = [];

    selectedTransactionTypes?: number[];
    paymentStatuses: SelectOptionLabelValue[] = [];
    selectedPaymentStatus?: number = 40;
    selectedTimezone?: string;
    selectedGroupBy: number = 0;
    selectedChannels?: number[];
    selectedStatusNames?: number[];

    @Input() selectedAnalysisType: string = 'Payments';

    @Input() creditors!: any[];
    @Input() timezones!: any[];

    todaysDate = new Date();

    chartOptions?: EChartsOption;
    countReturnedLookups: number = 0;

    ngOnInit(): void {
        if (this.selectedAnalysisType !== 'Placements') {
            this.getDropdownOptions();
        } else {
            this.getGraph();
        }
    }

    get selectedAnalysisTypeIcon(): string {
        switch (this.selectedAnalysisType) {
            case 'Placements':
                return 'calculator';

            case 'Communications':
                return 'comment';

            default:
                return 'wallet';
        }
    }

    incrementReturnedDropdownCount() {
        this.countReturnedLookups++;
        if (this.countReturnedLookups === 2) {
            this.getGraph();
        }
    }

    getDropdownOptions() {
        if (this.selectedAnalysisType === 'Payments') {
            this.getTransactionTypes();
            this.getPaymentStatuses();
        }

        if (this.selectedAnalysisType === 'Communications') {
            this.getChannels();
            this.getStatusNames();
        }
    }

    getPaymentStatuses() {
        this.dashboardService
            .getPaymentStatuses()
            .pipe(
                tap((res: any) => {
                    if (isArrayWithData(res)) {
                        this.paymentStatuses = res.filter((i: any) =>
                            [40, 30].includes(i.value),
                        );
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.incrementReturnedDropdownCount();
                }),
            )
            .subscribe();
    }

    getTransactionTypes() {
        this.dashboardService
            .getTransactionTypes()
            .pipe(
                tap((res: any) => {
                    if (isArrayWithData(res)) {
                        this.transactionTypes = res;
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.incrementReturnedDropdownCount();
                }),
            )
            .subscribe();
    }

    getChannels() {
        this.dashboardService
            .getChannels()
            .pipe(
                tap((res: any) => {
                    if (isArrayWithData(res)) {
                        this.channels = res;
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.incrementReturnedDropdownCount();
                }),
            )
            .subscribe();
    }

    getStatusNames() {
        this.dashboardService
            .getStatusNames()
            .pipe(
                tap((res: any) => {
                    if (isArrayWithData(res)) {
                        this.statusNames = res;
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.incrementReturnedDropdownCount();
                }),
            )
            .subscribe();
    }

    mockDataWithMapping = () => {
        const responseApi = [
            {
                groupName: 'Feb-1',
                groupDate: '2026-02-01',
                creditors: [
                    {
                        creditor: 'Advance America',
                        count: 122,
                    },
                    {
                        creditor: 'Balance Credit',
                        count: 6,
                    },
                ],
            },
            {
                groupName: 'Feb-2',
                groupDate: '2026-02-02',
                creditors: [
                    {
                        creditor: 'Balance Credit',
                        count: 20,
                    },
                    {
                        creditor: 'Advance America',
                        count: 210,
                    },
                ],
            },
            {
                groupName: 'Feb-3',
                groupDate: '2026-02-03',
                creditors: [
                    {
                        creditor: 'Advance America',
                        count: 137,
                    },
                    {
                        creditor: 'Balance Credit',
                        count: 9,
                    },
                ],
            },
            {
                groupName: 'Feb-4',
                groupDate: '2026-02-04',
                creditors: [
                    {
                        creditor: 'Advance America',
                        count: 371,
                    },
                    {
                        creditor: 'Balance Credit',
                        count: 27,
                    },
                ],
            },
            {
                groupName: 'Feb-5',
                groupDate: '2026-02-05',
                creditors: [
                    {
                        creditor: 'Advance America',
                        count: 281,
                    },
                    {
                        creditor: 'Balance Credit',
                        count: 33,
                    },
                ],
            },
        ];

        const mappedData = this.mapApiToGraphData(responseApi);
        this.buildChart(mappedData);
    };

    mockDataWithoutMapping = () => {
        this.buildChart([
            {
                name: 'Week 1',
                creditor: 'Acima (Early Stage)',
                count: 2287,
            },
            {
                name: 'Week 2',
                creditor: 'Acima (Early Stage)',
                count: 1701,
            },
            {
                name: 'Week 3',
                creditor: 'Acima (Early Stage)',
                count: 2100,
            },
            {
                name: 'Week 4',
                creditor: 'Acima (Early Stage)',
                count: 1900,
            },

            {
                name: 'Week 1',
                creditor: 'Westlake',
                count: 900,
            },
            {
                name: 'Week 2',
                creditor: 'Westlake',
                count: 1100,
            },
            {
                name: 'Week 3',
                creditor: 'Westlake',
                count: 950,
            },
            {
                name: 'Week 4',
                creditor: 'Westlake',
                count: 1000,
            },

            {
                name: 'Week 1',
                creditor: 'Snap Finance',
                count: 1500,
            },
            {
                name: 'Week 2',
                creditor: 'Snap Finance',
                count: 1400,
            },
            {
                name: 'Week 3',
                creditor: 'Snap Finance',
                count: 1300,
            },
            {
                name: 'Week 4',
                creditor: 'Snap Finance',
                count: 1250,
            },

            {
                name: 'Week 1',
                creditor: 'MoneyKey',
                count: 1234,
            },
            {
                name: 'Week 2',
                creditor: 'MoneyKey',
                count: 1234,
            },
            {
                name: 'Week 3',
                creditor: 'MoneyKey',
                count: 1100,
            },
            {
                name: 'Week 4',
                creditor: 'MoneyKey',
                count: 4100,
            },

            {
                name: 'Week 1',
                creditor: 'Mercantile',
                count: 900,
            },
            {
                name: 'Week 2',
                creditor: 'Mercantile',
                count: 2220,
            },
            {
                name: 'Week 3',
                creditor: 'Mercantile',
                count: 1950,
            },
            {
                name: 'Week 4',
                creditor: 'Mercantile',
                count: 3400,
            },

            {
                name: 'Week 1',
                creditor: 'Con Edison',
                count: 4500,
            },
            {
                name: 'Week 2',
                creditor: 'Con Edison',
                count: 3400,
            },
            {
                name: 'Week 3',
                creditor: 'Con Edison',
                count: 6300,
            },
            {
                name: 'Week 4',
                creditor: 'Con Edison',
                count: 1250,
            },
        ]);
    };

    onChangeSelectedGroupBy(event: any) {
        this.getGraph();
    }

    private mapApiToGraphData(apiResponse: any[]): GraphData[] {
        const flattened = apiResponse.flatMap((group: any) =>
            group.creditors.map((c: any) => ({
                name: group.groupName,
                creditor: c.creditor,
                count: c.count,
            })),
        );
        return flattened;
    }

    getGraph() {
        this.chartOptions = undefined;
        this.appService.isShowPageSpinner = true;
        this.dashboardService
            .getGraphTrendAnalysis(
                moment(this.fromDate).format('YYYY-MM-DD'),
                moment(this.toDate).format('YYYY-MM-DD'),
                this.selectedCreditors?.map((i: any) => i.value),
                this.selectedTimezone,
                this.selectedTransactionTypes,
                this.selectedPaymentStatus,
                this.selectedGroupBy,
                this.selectedAnalysisType,
                this.selectedChannels,
                this.selectedStatusNames,
            )
            .pipe(
                tap((responseApi: any) => {
                    if (isArrayWithData(responseApi)) {
                        const mappedData = this.mapApiToGraphData(responseApi);
                        this.buildChart(mappedData);
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.appService.isShowPageSpinner = false;
                }),
            )
            .subscribe();
    }

    private buildChart(data: GraphData[]): void {
        const creditors = [...new Set(data.map((x) => x.creditor))];
        const weeks = [...new Set(data.map((x) => x.name))];

        const series: BarSeriesOption[] = creditors.map((creditor) => ({
            name: creditor,
            type: 'bar',
            barMaxWidth: 40,
            data: weeks.map((week) => {
                const found = data.find(
                    (x) => x.creditor === creditor && x.name === week,
                );
                return found ? found.count : 0;
            }),
        }));

        this.chartOptions = {
            tooltip: { trigger: 'axis' },
            legend: { data: creditors },
            grid: { left: 60, right: 20, bottom: 60, top: 40 },
            xAxis: { type: 'category', data: weeks },
            yAxis: { type: 'value' },
            series,
        };
    }
}
